import type { StyleErrorCode } from "@/components/admin/ai-assistant/style-settings.logic";
import {
	type ExpressiveFrequency,
	STYLE_LIMITS,
	type StylePreset,
} from "@/types/ai-assistant-style";

/**
 * Teks bagian "Gaya & aturan Jenna" di /admin/ai-assistant (id/en).
 * `{name}` = nama asisten, `{n}`/`{max}` = penghitung, `{word}` = kata ekspresif.
 */

export interface AssistantStyleText {
	title: string;
	description: string;
	core: {
		title: string;
		description: string;
		larangan: string;
		pengganti: string;
	};
	styles: {
		chat: string;
		voice: string;
		presets: Record<StylePreset, string>;
	};
	words: {
		title: string;
		description: string;
		placeholder: string;
		add: string;
		remove: string;
		formalHint: string;
		frequency: string;
		frequencies: Record<ExpressiveFrequency, string>;
	};
	rules: {
		title: string;
		description: string;
		larangan: string;
		pengganti: string;
		aktif: string;
		remove: string;
		add: string;
		empty: string;
		counter: string;
	};
	preview: { title: string; description: string; chat: string; voice: string };
	errors: Record<StyleErrorCode, string>;
	save: string;
	saved: string;
	unavailable: string;
}

const id: AssistantStyleText = {
	title: "Gaya & aturan {name}",
	description:
		"Atur nada bicara per kanal dan larangan tambahan. Aturan inti selalu berlaku dan tidak bisa dilonggarkan.",
	core: {
		title: "Aturan inti (selalu berlaku)",
		description:
			"Terkunci di sistem; larangan tambahan di bawah hanya bisa menambah.",
		larangan: "Larangan",
		pengganti: "Gantinya",
	},
	styles: {
		chat: "Gaya chat (ketik)",
		voice: "Gaya suara",
		presets: { formal: "Formal", ramah: "Ramah", santai: "Santai" },
	},
	words: {
		title: "Kata ekspresif",
		description:
			"Paling banyak satu per jawaban, tidak pernah di kalimat berisi angka atau kesimpulan data.",
		placeholder: "Tambah kata, mis. Siap",
		add: "Tambah",
		remove: "Hapus {word}",
		formalHint: "Tidak dipakai pada gaya Formal.",
		frequency: "Frekuensi",
		frequencies: { jarang: "Jarang", sedang: "Sedang" },
	},
	rules: {
		title: "Larangan tambahan",
		description: "Hanya larangan yang aktif yang masuk ke instruksi {name}.",
		larangan: "Larangan",
		pengganti: "Gantinya lakukan",
		aktif: "Aktif",
		remove: "Hapus larangan",
		add: "Tambah larangan",
		empty: "Belum ada larangan tambahan.",
		counter: "{n}/{max}",
	},
	preview: {
		title: "Pratinjau",
		description: "Contoh statis dari template, bukan jawaban AI.",
		chat: "Chat",
		voice: "Suara",
	},
	errors: {
		preset: "Pilih gaya yang tersedia.",
		frequency: "Pilih frekuensi yang tersedia.",
		waitVolume: "Pilih volume yang tersedia.",
		wordsMax: "Maksimal {max} kata ekspresif.",
		wordChars: "Kata ekspresif maksimal {max} karakter.",
		rulesMax: "Maksimal {max} larangan tambahan.",
		ruleEmpty: "Wajib diisi.",
		ruleChars: "Maksimal {max} karakter.",
		waitPhrasesMin: "Isi minimal {max} kalimat tunggu.",
		waitPhrasesMax: "Maksimal {max} kalimat tunggu.",
		waitPhraseChars: "Kalimat tunggu maksimal {max} karakter.",
	},
	save: "Simpan gaya & aturan",
	saved: "Tersimpan",
	unavailable:
		"Endpoint gaya belum tersedia di server ini; menampilkan nilai default.",
};

const en: AssistantStyleText = {
	title: "{name}'s style & rules",
	description:
		"Set the speaking tone per channel and extra prohibitions. Core rules always apply and cannot be loosened.",
	core: {
		title: "Core rules (always apply)",
		description:
			"Locked in the system; extra prohibitions below can only add to them.",
		larangan: "Prohibited",
		pengganti: "Instead",
	},
	styles: {
		chat: "Chat style (typed)",
		voice: "Voice style",
		presets: { formal: "Formal", ramah: "Friendly", santai: "Casual" },
	},
	words: {
		title: "Expressive words",
		description:
			"At most one per answer, never in a sentence with numbers or data conclusions.",
		placeholder: "Add a word, e.g. Siap",
		add: "Add",
		remove: "Remove {word}",
		formalHint: "Not used with the Formal style.",
		frequency: "Frequency",
		frequencies: { jarang: "Rarely", sedang: "Sometimes" },
	},
	rules: {
		title: "Extra prohibitions",
		description: "Only active prohibitions are added to {name}'s instructions.",
		larangan: "Prohibited",
		pengganti: "Do this instead",
		aktif: "Active",
		remove: "Remove prohibition",
		add: "Add prohibition",
		empty: "No extra prohibitions yet.",
		counter: "{n}/{max}",
	},
	preview: {
		title: "Preview",
		description: "A static template example, not an AI answer.",
		chat: "Chat",
		voice: "Voice",
	},
	errors: {
		preset: "Choose an available style.",
		frequency: "Choose an available frequency.",
		waitVolume: "Choose an available volume.",
		wordsMax: "At most {max} expressive words.",
		wordChars: "Expressive words are at most {max} characters.",
		rulesMax: "At most {max} extra prohibitions.",
		ruleEmpty: "Required.",
		ruleChars: "At most {max} characters.",
		waitPhrasesMin: "Add at least {max} waiting sentence.",
		waitPhrasesMax: "At most {max} waiting sentences.",
		waitPhraseChars: "Waiting sentences are at most {max} characters.",
	},
	save: "Save style & rules",
	saved: "Saved",
	unavailable:
		"The style endpoint is not available on this server; showing defaults.",
};

export const assistantStyleTexts = { id, en } as const;

/** Ganti `{kunci}` di template dengan nilai. */
export function fillStyleText(
	template: string,
	vars: Record<string, string | number>,
): string {
	return template.replace(/\{(\w+)\}/g, (m, key: string) =>
		key in vars ? String(vars[key]) : m,
	);
}

const ERROR_MAX: Partial<Record<StyleErrorCode, number>> = {
	wordsMax: STYLE_LIMITS.expressiveWordsMax,
	wordChars: STYLE_LIMITS.expressiveWordCharsMax,
	rulesMax: STYLE_LIMITS.extraRulesMax,
	ruleChars: STYLE_LIMITS.extraRuleCharsMax,
	waitPhrasesMin: STYLE_LIMITS.waitPhrasesMin,
	waitPhrasesMax: STYLE_LIMITS.waitPhrasesMax,
	waitPhraseChars: STYLE_LIMITS.waitPhraseCharsMax,
};

/** Pesan error validasi dalam bahasa UI. */
export function styleErrorText(
	t: AssistantStyleText,
	code: StyleErrorCode,
): string {
	return fillStyleText(t.errors[code], { max: ERROR_MAX[code] ?? "" });
}
